import { HTTP } from '@awesome-cordova-plugins/http';

const DEFAULT_JSON_HEADERS = {
  'Content-type': 'application/json',
  Accept: 'application/json',
};

// We check for Cordova to determine if we should use the native HTTP plugin or the Fetch API (ie. for e2e tests).
const isCordovaAvailable = () =>
  typeof window !== 'undefined' && (window as unknown as Record<string, unknown>).cordova;

// @TODO: consider refactoring to a singleton class which instantiates per wifi setup flow, e.g. one for meter, inverter
//        and others. main benefit here is that we can set the base IP address on each instance

/**
 * Helper function to assert response types from the native HTTP wrapper.
 *
 * @param url - The full URL to call, including the base domain (e.g. `https://google.com`)
 * @param headers - Any custom headers you might want to apply, defaulted to sensible JSON values.
 */
export async function get<T>(url: string, headers: Record<string, string> = DEFAULT_JSON_HEADERS): Promise<T> {
  /* istanbul ignore if -- @preserve */
  if (isCordovaAvailable()) {
    const response = await HTTP.get(url, null, headers);
    // Response might be undefined, which can't be parsed as JSON.
    return response.data ? (JSON.parse(response.data) as T) : (response.data as T);
  } else {
    const res = await fetch(url, { method: 'GET', headers });
    if (!res.ok) throw new Error(`GET failed: ${res.status}`);
    const text = await res.text();
    return text ? (JSON.parse(text) as T) : ({} as T);
  }
}

/**
 * Helper function to assert response types from the native HTTP wrapper.
 *
 * @param url - The full URL to call, including the base domain (e.g. `https://google.com`)
 * @param body - The body to be sent as the payload of the HTTP POST request.
 * @param headers - Any custom headers you might want to apply, defaulted to sensible JSON values.
 */
export async function post<T>(
  url: string,
  body: object,
  headers: Record<string, string> = DEFAULT_JSON_HEADERS
): Promise<T> {
  /* istanbul ignore if -- @preserve */
  if (isCordovaAvailable()) {
    const response = await HTTP.post(url, body, headers);
    // Response might be undefined, which can't be parsed as JSON.
    return response.data ? (JSON.parse(response.data) as T) : (response.data as T);
  } else {
    const res = await fetch(url, {
      method: 'POST',
      headers,
      body: JSON.stringify(body),
    });
    if (!res.ok) throw new Error(`POST failed: ${res.status}`);
    const text = await res.text();
    return text ? (JSON.parse(text) as T) : ({} as T);
  }
}

/**
 * Helper function to assert response types from the native HTTP wrapper.
 *
 * @param url - The full URL to call, including the base domain (e.g. `https://google.com`)
 * @param body - The body to be sent as the payload of the HTTP PUT request.
 * @param headers - Any custom headers you might want to apply, defaulted to sensible JSON values.
 */

/* istanbul ignore next -- @preserve */
export async function put<T>(
  url: string,
  body: object,
  headers: Record<string, string> = DEFAULT_JSON_HEADERS
): Promise<T> {
  if (isCordovaAvailable()) {
    const response = await HTTP.put(url, body, headers);
    // Response might be undefined, which can't be parsed as JSON.
    return JSON.parse(response.data) as T;
  } else {
    const res = await fetch(url, {
      method: 'PUT',
      headers,
      body: JSON.stringify(body),
    });
    if (!res.ok) throw new Error(`PUT failed: ${res.status}`);
    const text = await res.text();
    return text ? (JSON.parse(text) as T) : ({} as T);
  }
}

/**
 * Helper function to assert response types from the native HTTP wrapper.
 *
 * @param url - The full URL to call, including the base domain (e.g. `https://google.com`)
 * @param body - The body to be sent as the payload of the HTTP PATCH request.
 * @param headers - Any custom headers you might want to apply, defaulted to sensible JSON values.
 */

/* istanbul ignore next -- @preserve */
export async function patch<T>(
  url: string,
  body: object,
  headers: Record<string, string> = DEFAULT_JSON_HEADERS
): Promise<T> {
  if (isCordovaAvailable()) {
    const response = await HTTP.patch(url, body, headers);
    // Response might be undefined, which can't be parsed as JSON.
    return response.data ? (JSON.parse(response.data) as T) : (response.data as T);
  } else {
    const res = await fetch(url, {
      method: 'PATCH',
      headers,
      body: JSON.stringify(body),
    });
    if (!res.ok) throw new Error(`PATCH failed: ${res.status}`);
    const text = await res.text();
    return text ? (JSON.parse(text) as T) : ({} as T);
  }
}

/**
 * Helper function to assert response types from the native HTTP wrapper.
 *
 * @param url - The full URL to call, including the base domain (e.g. `https://google.com`)
 * @param headers - Any custom headers you might want to apply, defaulted to sensible JSON values.
 */

/* istanbul ignore next -- @preserve */
export async function del<T>(url: string, headers: Record<string, string> = DEFAULT_JSON_HEADERS): Promise<T> {
  if (isCordovaAvailable()) {
    const response = await HTTP.delete(url, null, headers);
    // Response might be undefined, which can't be parsed as JSON.
    return response.data ? (JSON.parse(response.data) as T) : (response.data as T);
  } else {
    const res = await fetch(url, {
      method: 'DELETE',
      headers,
    });
    if (!res.ok) throw new Error(`DELETE failed: ${res.status}`);
    const text = await res.text();
    return text ? (JSON.parse(text) as T) : ({} as T);
  }
}
